/**
 * Suspend / resume <model-viewer> elements based on viewport proximity to
 * reduce GPU / memory pressure on devices with limited WebGL resources.
 *
 * Behaviour:
 * - Initial load is always allowed (so dependent components can await
 *   `model.loaded`).
 * - Once a model-viewer has loaded at least once AND has left the proximity
 *   zone, its `src` is cleared (model data released).
 * - When the element re-enters the proximity zone, the original `src` is
 *   restored and the model reloads from cache.
 */
export function initLazyModelViewers() {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return;
  }

  const elements = document.querySelectorAll('model-viewer');
  if (elements.length === 0) return;

  const state = new WeakMap();

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        const info = state.get(el);
        if (!info) return;

        if (entry.isIntersecting) {
          if (el.getAttribute('src') !== info.src) {
            el.setAttribute('src', info.src);
          }
        } else if (info.loaded && el.getAttribute('src')) {
          el.setAttribute('src', '');
        }
      });
    },
    { rootMargin: '100% 0px' },
  );

  elements.forEach((el) => {
    const src = el.getAttribute('src');
    if (!src) return;

    state.set(el, { src, loaded: false });

    const onLoad = () => {
      const info = state.get(el);
      if (info) info.loaded = true;
    };

    if (el.loaded) {
      onLoad();
    } else {
      el.addEventListener('load', onLoad, { once: true });
    }

    observer.observe(el);
  });
}
